import type {
  PortalBucketCoverage,
  PortalLocationCrm,
  PortalLocationFigures,
  PortalLocationRow,
} from '../../types/portalReport.js';
import { deriveCostPerSale, type CrmClubTotals } from './crm.js';

// CLIENT PORTAL — the ALL LOCATIONS leaderboard (§4 phase A, 2026-09-03).
//
// Scott, 2026-09-02: "Making everything appear so we can see individual club
// results whilst also seeing combined results… a report the managers and owners
// use to get a snapshot on the overall results."
//
// PURE, and SERVER-SAFE — no `computeDelta`, which lives in a client module the
// server cannot import (see deltas.ts's header). Assembly happens here; the
// PERCENTAGES are taken client-side by `buildLocationDeltas`.
//
// ⚠️⚠️ A READER GIVEN PER-CLUB AND COMBINED FIGURES ON ONE PAGE WILL SUBTRACT,
// and the two do not relate the same way for every column: per-club COUNTS sum
// to the combined total, and COST PER LEAD DOES NOT — it is re-derived per club
// from that club's own spend and leads, exactly as the combined figure is
// re-derived from the combined pair. Averaging two clubs' cost per lead produces
// a number that describes neither. `leaderboardNestingNote` is what says so on
// the page.

/**
 * Whether a club's own history covers the selected period.
 *
 * ⚠️⚠️ ONE COMBINED SERIES HAS ONE COVERAGE; TWELVE CLUBS HAVE TWELVE. This is
 * the per-club multiplication of the report's oldest rule: a club that was not
 * being measured in the selected period renders an em dash, never a 0. A zero on
 * a leaderboard reads as "this club produced nothing", which is a materially
 * different — and on a franchise report commercially damaging — statement from
 * "we were not measuring this club yet".
 *
 * ⚠️ `partial` is REAL BUT UNDERSTATING (the club's history starts inside the
 * period), so its figures ARE shown and flagged. Suppressing them would hide a
 * real result; showing them unflagged would invite a comparison against a
 * fully-covered club beside it.
 *
 * ⚠️ NO `in-progress` state here. That is a property of a BUCKET that has not
 * finished, and a leaderboard row is a period total rather than a bucket — the
 * period note already tells the reader today is still running.
 */
export function locationCoverage(
  firstDate: string | null,
  period: { from: string; to: string },
): PortalBucketCoverage {
  if (!firstDate) return 'no-data';
  if (firstDate > period.to) return 'no-data';
  if (firstDate > period.from) return 'partial';
  return 'complete';
}

export interface LocationScopeAccount {
  accountId: string;
  name: string;
}

/**
 * One row exactly as a per-account query returns it.
 *
 * ⚠️ DELIBERATELY NOT `PortalLocationRow`. A query row is always fully
 * measured — it exists because there was delivery — so its figures are plain
 * numbers, while the ROW THIS BUILDS carries nulls for a club the query never
 * mentioned. Reusing the wire type here would make every field nullable on the
 * one side where a null is impossible, and the nulls would stop meaning
 * anything.
 */
export interface LocationQueryRow {
  accountId: string;
  currency: string;
  leads: number;
  spend: number;
  costPerLead: number | null;
}

export interface BuildLocationRowsInput {
  /** Every account the client holds — the leaderboard lists ALL of them. */
  accounts: readonly LocationScopeAccount[];
  period: { from: string; to: string };
  /** Rows the period query returned. A club absent from here had no delivery. */
  measured: readonly LocationQueryRow[];
  /** Rows the COMPARISON-window query returned, same shape. */
  previous: readonly LocationQueryRow[];
  /** Per-account first measured date on the channel(s) this leaderboard covers. */
  firstDates: readonly { accountId: string; firstDate: string }[];
  /**
   * Why the comparison cannot be shown, when it cannot — the report's OWN
   * reason, for the window the client selected.
   *
   * ⚠️ NOT re-derived here. The leaderboard disagreeing with the hero about "vs
   * what" is worse than having no deltas at all.
   */
  comparisonUnavailableReason: string | null;
  /** The comparison window, so each club's own coverage of it can be checked. */
  previousPeriod: { from: string; to: string } | null;
  /** Per-club CRM figures, joined by kyck account id. OVERVIEW ONLY. */
  crmByAccountId?: ReadonlyMap<string, PortalLocationCrm>;
}

/**
 * Every club in scope, as one row each.
 *
 * ⚠️⚠️ EVERY CLUB, INCLUDING ONE WITH NO DELIVERY. The period query returns only
 * clubs that HAVE rows, so before this a club with nothing in the period was
 * simply absent from the table — and a manager looking for their club found
 * nothing at all, which reads as a broken report rather than as a quiet month. A
 * club that was MEASURED and delivered nothing is a real zero and shows 0; one
 * that was not measured shows dashes.
 */
export function buildLocationRows(input: BuildLocationRowsInput): PortalLocationRow[] {
  const firstByAccount = new Map(
    input.firstDates.filter((d) => !!d.firstDate).map((d) => [d.accountId, d.firstDate]),
  );
  const measuredByAccount = foldByAccount(input.measured);
  const previousByAccount = foldByAccount(input.previous);

  const rows: PortalLocationRow[] = input.accounts.map((a) => {
    const firstDate = firstByAccount.get(a.accountId) ?? null;
    const coverage = locationCoverage(firstDate, input.period);
    const cur = measuredByAccount.get(a.accountId);
    const prev = previousByAccount.get(a.accountId);
    const prevCoverage = input.previousPeriod
      ? locationCoverage(firstDate, input.previousPeriod)
      : 'no-data';

    // ⚠️⚠️ A CLUB'S OWN COMPARISON NEEDS THE CLUB'S OWN COVERAGE. The report's
    // reason answers "is the comparison window covered for this SCOPE"; a club
    // whose own history starts inside that window would otherwise carry a delta
    // computed against a partial count and reported as a collapse. Both gates
    // apply, and either one refusing is enough.
    const prevFigures: PortalLocationFigures | null =
      input.comparisonUnavailableReason === null && prevCoverage === 'complete' && prev
        ? { leads: prev.leads, spend: prev.spend, costPerLead: prev.costPerLead }
        : null;
    // ⚠️ A club whose two windows are in DIFFERENT currencies gets no money
        // comparison. Its leads still compare — a lead is a lead whoever paid.
    const comparable = prevFigures && cur && prev && cur.currency === prev.currency;

    return {
      accountId: a.accountId,
      accountName: a.name,
      // ⚠️ The currency comes from a row that HAS one. A club with no delivery
      // has no currency of its own to report, and defaulting it to the first
      // club's would label one country's dashes with another's symbol.
      currency: cur?.currency ?? prev?.currency ?? null,
      coverage,
      leads: coverage === 'no-data' ? null : (cur?.leads ?? 0),
      spend: coverage === 'no-data' ? null : (cur?.spend ?? null),
      // ⚠️ RE-DERIVED per club by the query layer (spend ÷ leads), never
      // averaged. A club with no leads has NO cost per lead — null, never a
      // spend figure divided by nothing and never 0, which would read as "leads
      // were free" on the one page where that misstatement flatters us most.
      costPerLead: coverage === 'no-data' ? null : (cur?.costPerLead ?? null),
      previous: prevFigures
        ? {
            leads: prevFigures.leads,
            spend: comparable ? prevFigures.spend : null,
            costPerLead: comparable ? prevFigures.costPerLead : null,
          }
        : null,
      crm: input.crmByAccountId?.get(a.accountId) ?? null,
    };
  });

  return rows.sort((x, y) => x.accountName.localeCompare(y.accountName));
}

/**
 * One entry per account, folding a club that came back in more than one currency.
 *
 * ⚠⚠ THE QUERY GROUPS BY (account, currency), so a club whose ad account
 * changed currency mid-history returns TWO rows. Counts fold safely — a lead is
 * a lead whoever paid — but the MONEY DOES NOT: summing AUD into NZD produces a
 * figure that exists in no currency at all, which is this report's oldest money
 * rule. So a multi-currency club keeps its leads and loses its spend and cost
 * per lead to nulls, which render as dashes.
 *
 * ⚠ `null` currency is the signal, not a flag: `moneyRound` cannot be called
 * without one, so the dash is structural rather than remembered.
 */
function foldByAccount(
  rows: readonly LocationQueryRow[],
): Map<string, { currency: string | null; leads: number; spend: number | null; costPerLead: number | null }> {
  const out = new Map<
    string,
    { currency: string | null; leads: number; spend: number | null; costPerLead: number | null }
  >();
  for (const r of rows) {
    const existing = out.get(r.accountId);
    if (!existing) {
      out.set(r.accountId, {
        currency: r.currency,
        leads: r.leads,
        spend: r.spend,
        costPerLead: r.costPerLead,
      });
      continue;
    }
    existing.leads += r.leads;
    if (existing.currency === r.currency && existing.spend !== null) {
      existing.spend = round2(existing.spend + r.spend);
      // ⚠ RE-DERIVED from the summed pair, never averaged from the two rows'
      // cost-per-lead figures — the same rule every ratio on this report follows.
      existing.costPerLead = existing.leads > 0 ? round2(existing.spend / existing.leads) : null;
    } else {
      existing.currency = null;
      existing.spend = null;
      existing.costPerLead = null;
    }
  }
  return out;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Turn per-club CRM totals into the leaderboard's CRM columns.
 *
 * ⚠️⚠️ A CLUB WITH NO CRM LINK GETS NO ENTRY AT ALL, so its columns render as
 * dashes. `countedClubs` / `uncountedClubs` already exist on `PortalCrmBlock`
 * precisely because an unlinked club summing in as a zero is rule 15 — and
 * `splitCrmClubs` already counts a club only when it has a `crmId` AND a real
 * first date in the CRM source, so a `crmId` matching no club lands in
 * `uncounted` and is NAMED rather than silently dropped or zeroed.
 */
export function crmByAccount(
  countedAccountIds: readonly string[],
  crmIdByAccountId: ReadonlyMap<string, string>,
  rows: readonly CrmClubTotals[],
  previousRows: readonly CrmClubTotals[] | null,
): Map<string, PortalLocationCrm> {
  const byCrmId = new Map(rows.map((r) => [r.crmId, r]));
  const prevByCrmId = new Map((previousRows ?? []).map((r) => [r.crmId, r]));
  const out = new Map<string, PortalLocationCrm>();
  for (const accountId of countedAccountIds) {
    const crmId = crmIdByAccountId.get(accountId);
    if (!crmId) continue;
    const r = byCrmId.get(crmId);
    const p = previousRows ? prevByCrmId.get(crmId) : undefined;
    out.set(accountId, {
      // ⚠️ A COUNTED club with no row is a measured zero — it has CRM history,
      // it simply took no enquiries in this period. That is a real 0, unlike an
      // unlinked club, which never reaches this map at all.
      leads: r?.leads ?? 0,
      digitalLeads: r?.digitalLeads ?? 0,
      sales: r?.sales ?? 0,
      previous: p ? { leads: p.leads, digitalLeads: p.digitalLeads, sales: p.sales } : null,
    });
  }
  return out;
}

/**
 * One club's cost per sale: that club's ad spend ÷ that club's CRM sales.
 *
 * ⚠️⚠️ A CROSS-SOURCE RATIO — ad spend over a CRM count — and per club it is
 * legal only because CLUB IS A DIMENSION OF BOTH HALVES. A sale carries no
 * channel and no source (blank `source`, `contact_type` AND `person_id`, 188,726
 * of 188,726), so there is no per-CHANNEL version of this and there never will
 * be from these rows.
 *
 * ⚠️ ALL MEDIA OVER ALL SALES, walk-ins and referrals included. The panel-level
 * figure carries that caveat and this column inherits it; it is not a
 * cost-per-sale for the advertising.
 *
 * ⚠️⚠️ A CLUB THE CRM DOES NOT COVER GETS `null`, NOT A RATIO AGAINST ZERO. An
 * unlinked club has `crm: null` — it never reached `crmByAccount` — and dividing
 * its real spend by an assumed zero would either divide by nothing or, worse,
 * print a spend figure as though it bought no members.
 *
 * ⚠️ NULL without a currency too: the money half has to be attributable to one
 * currency, and a club that returned two has already lost its spend to null.
 */
export function locationCostPerSale(row: PortalLocationRow): number | null {
  if (row.crm === null) return null;
  if (row.spend === null || row.currency === null) return null;
  return deriveCostPerSale(row.spend, row.crm.sales);
}

export interface GroupAverage {
  /** The average itself, or null when nothing was measured. */
  value: number | null;
  /** ⚠️ CLUBS WITH DATA — the denominator, which the tile must state. */
  clubs: number;
  /** How many clubs are in scope, so the tile can say "of N". */
  clubsInScope: number;
  previous: number | null;
}

/**
 * The two group-average tiles: leads per club, and sales per club.
 *
 * ⚠️⚠️ THE DENOMINATOR IS THE CLUBS WITH DATA, NOT THE CLUBS IN SCOPE, and the
 * tile STATES it. A club that has never run ads dragging the average down is
 * rule 15 at aggregate scale — an unmeasured thing counted as a measured nothing
 * — and a rate without its denominator beside it is this report's own standing
 * rule.
 *
 * ⚠️⚠️ COUNTS ONLY. There is deliberately no "average spend per club" and no
 * "average cost per lead per club": an ANZ group would produce a figure that
 * exists in no currency at all. Leads and sales are counts and blend safely — a
 * lead is a lead whoever paid. If a single-currency scope ever makes a money
 * average tempting, the `A$` / `NZ$` disambiguation is a PREREQUISITE of that
 * work rather than a follow-up to it.
 */
export function groupAverages(rows: readonly PortalLocationRow[]): {
  leads: GroupAverage;
  sales: GroupAverage;
} {
  const measured = rows.filter((r) => r.coverage !== 'no-data' && r.leads !== null);
  const withCrm = rows.filter((r) => r.crm !== null);

  const mean = (n: number, d: number): number | null => (d > 0 ? n / d : null);

  /**
   * ⚠️⚠️ COMPLETE OR ABSENT — THE PRIOR AVERAGE IS OVER THE **SAME CLUB SET** AS
   * THE CURRENT ONE, OR THERE IS NO DELTA AT ALL.
   *
   * FOUND BY LOOKING AT RENDERED OUTPUT, 2026-09-03, and the first version of
   * this file had the bug while its own comment warned against it: `value`
   * divided by every measured club and `previous` divided by the clubs that
   * happened to have a prior half. On a four-club fixture that printed
   * "21.7 leads per club, ▼ −29%" — a three-club average against a two-club
   * average, which is not a change in performance at all. A per-club average
   * whose denominator moves between the two halves is the aggregate form of
   * comparing periods of different lengths.
   *
   * ⚠️ ALL-OR-NOTHING rather than narrowing the CURRENT average to the clubs
   * with priors: the headline figure must describe the whole group the reader
   * can see in the table beneath it. Suppressing the delta is the honest
   * trade — the same complete-or-absent contract `PortalYearOnYear` uses.
   */
  const comparableMean = <T,>(
    clubs: readonly T[],
    current: (c: T) => number,
    prior: (c: T) => number | null | undefined,
  ): number | null => {
    if (clubs.length === 0) return null;
    let total = 0;
    for (const c of clubs) {
      const p = prior(c);
      if (p == null) return null;
      total += p;
    }
    return total / clubs.length;
  };

  return {
    leads: {
      value: mean(
        measured.reduce((s, r) => s + (r.leads ?? 0), 0),
        measured.length,
      ),
      clubs: measured.length,
      clubsInScope: rows.length,
      previous: comparableMean(
        measured,
        (r) => r.leads ?? 0,
        (r) => r.previous?.leads,
      ),
    },
    sales: {
      value: mean(
        withCrm.reduce((s, r) => s + (r.crm?.sales ?? 0), 0),
        withCrm.length,
      ),
      clubs: withCrm.length,
      clubsInScope: rows.length,
      previous: comparableMean(
        withCrm,
        (r) => r.crm?.sales ?? 0,
        (r) => r.crm?.previous?.sales,
      ),
    },
  };
}

/**
 * ⚠️ Whether a leaderboard has anything to say. A single-location scope has no
 * by-location story — one row repeating the hero is noise — so it renders
 * nothing at all rather than a one-row table.
 */
export function hasLeaderboard(rows: readonly PortalLocationRow[] | null): boolean {
  return !!rows && rows.length > 1;
}
